import { useRouter } from '@tanstack/react-router'
import { ChevronsUpDown, LogOut } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/stores/authStore'
import { toast } from '@/hooks/use-toast'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar'

export function NavUser({
  user,
}: {
  user: {
    name: string
    email: string
    avatar: string
  }
}) {
  const { isMobile } = useSidebar()
  const auth = useAuth()
  const router = useRouter()
  const { t } = useTranslation()

  // Check if backup token exists to determine if we're impersonating
  const isImpersonating = !!localStorage.getItem('backupAccessToken')

  const handleLogout = () => {
    if (isImpersonating) {
      // Restore from impersonation
      auth.restoreFromImpersonation()
      toast({
        title: 'Restored Session',
        description: 'You have been logged back in as your original user.',
      })
      // Refresh the page to update the UI
      window.location.reload()
    } else {
      // Normal logout
      auth.reset()
      toast({
        title: t('messages.logoutSuccess'),
        description: t('messages.logoutSuccess'),
      })
      router.navigate({ to: '/sign-in' })
      window.location.reload()
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
            >
              <Avatar className='h-8 w-8 rounded-lg'>
                <AvatarImage src={user.avatar} alt={user.name} />
                <AvatarFallback className='rounded-lg'>SN</AvatarFallback>
              </Avatar>
              <div className='grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>
                  {isImpersonating ? `Login as ${user.name}` : user.name}
                </span>
                <span className='truncate text-xs'>{user.email}</span>
                {isImpersonating && (
                  <span className='truncate text-xs font-medium text-orange-600'>
                    Impersonating
                  </span>
                )}
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            align='end'
            sideOffset={4}
          >
            <DropdownMenuLabel className='p-0 font-normal'>
              <div className='flex items-center gap-2 px-1 py-1.5 text-left text-sm'>
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarImage src={user.avatar} alt={user.name} />
                  <AvatarFallback className='rounded-lg'>SN</AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>
                    {isImpersonating ? `Login as ${user.name}` : user.name}
                  </span>
                  <span className='truncate text-xs'>{user.email}</span>
                  {isImpersonating && (
                    <span className='truncate text-xs font-medium text-orange-600'>
                      Impersonating
                    </span>
                  )}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut />
              {isImpersonating ? 'Exit Impersonation' : t('auth.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
